import React, { useState, useEffect } from "react";
import { FiX, FiCheck } from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";

/**
 * SettleOpeningBalanceModal — record a payment against a customer's
 * opening balance (the amount they owed before the system started).
 *
 * Creates a normal receipt (R-series) via POST /api/client-receipts with
 * obAllocatedAmount. It is a stand-alone receipt — it is NOT mixed with bills,
 * wallet credit or TDS. To pay bills as well, make a separate receipt.
 *
 * Props
 *   business  – { id, companyName, contactPersonName?, contactPersonPhone? }
 *   summary   – { remaining, amount, settled, date }  (from GET /business/:id/opening-balance)
 *   onClose   – close the popup
 *   onSaved   – called with the fresh opening-balance summary after saving
 */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 };
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px",
  border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none",
  fontFamily: "var(--font-body)",
};

const today = () => new Date().toISOString().slice(0, 10);

const SettleOpeningBalanceModal = ({ business, summary, onClose, onSaved }) => {
  const remaining = Number(summary?.remaining || 0);

  const [form, setForm] = useState({
    amount: remaining > 0 ? String(remaining) : "",
    date: today(),
    mode: "cash",
    bankAccountId: "",
    note: "",
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [nextNumber, setNextNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    API.get("/company-bank-accounts", { params: { active: "true" } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
    API.get("/client-receipts/next-number")
      .then(({ data }) => setNextNumber(data.receiptNumber || ""))
      .catch(() => {});
  }, []);

  const amount = Number(form.amount);
  const minDate = summary?.date ? String(summary.date).slice(0, 10) : "";
  const afterBalance = Math.max(0, remaining - (Number.isFinite(amount) ? amount : 0));

  const handleSave = async () => {
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Enter the amount received.");
    if (amount > remaining + 0.009) return toast.error(`Amount cannot exceed the pending ${money(remaining)}.`);
    if (!form.date) return toast.error("Select the receipt date.");
    if (minDate && form.date < minDate) return toast.error(`Date cannot be before the opening balance date (${minDate}).`);

    try {
      setSaving(true);
      const { data } = await API.post("/client-receipts", {
        businessId:        business.id,
        customerName:      business.contactPersonName || business.companyName,
        customerPhone:     business.contactPersonPhone || "",
        companyName:       business.companyName,
        obAllocatedAmount: amount,
        receiptDate:       form.date,
        mode:              form.mode,
        bankAccountId:     form.bankAccountId || null,
        bankAccountLabel:  bankAccounts.find((a) => String(a.id) === String(form.bankAccountId))?.account_name || null,
        note:              form.note,
      });
      toast.success(`Receipt ${data.receipt?.receiptNumber || ""} saved — opening balance settled`);
      onSaved && onSaved(data.openingBalance);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 26, width: 500, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Settle Opening Balance</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{business.companyName}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}><FiX size={18} /></button>
        </div>

        {nextNumber && (
          <div style={{ margin: "8px 0 12px", display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 7, fontSize: 13 }}>
            Receipt No: <strong style={{ color: "#16a34a" }}>{nextNumber}</strong>
            <span style={{ fontSize: 11, color: "#6b7280" }}>(auto-assigned)</span>
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 13 }}>
          <span>Opening: <strong>{money(summary?.amount)}</strong></span>
          <span style={{ color: "#16a34a" }}>Settled: <strong>{money(summary?.settled)}</strong></span>
          <span style={{ color: "#dc2626" }}>Pending: <strong>{money(remaining)}</strong></span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Amount Received (₹) *</label>
            <input type="number" min="0" max={remaining} step="0.01" value={form.amount}
              onChange={(e) => set("amount", e.target.value)} placeholder={`Max ${money(remaining)}`} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date *</label>
            <input type="date" value={form.date} min={minDate || undefined}
              onChange={(e) => set("date", e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()} style={{ ...inputStyle, cursor: "pointer" }} />
          </div>
          <div>
            <label style={labelStyle}>Mode</label>
            <select value={form.mode} onChange={(e) => { set("mode", e.target.value); set("bankAccountId", ""); }} style={inputStyle}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <input type="text" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Received In Account</label>
            <select value={form.bankAccountId} onChange={(e) => set("bankAccountId", e.target.value)} style={inputStyle}>
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter((a) => {
                  if (form.mode === "cash") return a.account_type === "cash";
                  if (form.mode === "upi") return a.account_type === "upi";
                  if (form.mode === "bank_transfer" || form.mode === "cheque") return a.account_type === "bank";
                  return true;
                })
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ""}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {amount > 0 && (
          <div style={{ fontSize: 12.5, color: amount > remaining + 0.009 ? "#dc2626" : "#374151", marginBottom: 12 }}>
            Settles <strong>{money(amount)}</strong> of the opening balance · pending after: <strong>{money(afterBalance)}</strong>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 14 }}>
          This receipt only settles the opening balance. To record payment for bills, create a separate receipt.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "9px 18px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 14, fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#16a34a", color: "#fff", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontSize: 14, display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
            <FiCheck size={14} /> {saving ? "Saving…" : "Save Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettleOpeningBalanceModal;
